import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/Auth/authContext';
import { useI18n } from '../../contexts/I18nContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import { ChevronDownIcon, LogOutIcon } from './icons';

export const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return (first + last).toUpperCase();
};

/**
 * The account control in the top bar: an avatar that opens a small panel
 * with who you are, which space you belong to, your roles, and the one
 * action — sign out. Signing out used to hide behind a hover on the
 * sidebar's name button, where nothing announced it.
 */
export const UserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const { isAnepStaff } = useClientAccess();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on a click anywhere else or on Escape. Registered only while
  // open so an idle page carries no listeners.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!user) return null;

  const displayName = user.name || user.preferredUsername || user.email;
  const initials = initialsOf(displayName);

  return (
    <div className="user-menu" ref={rootRef}>
      <button
        type="button"
        className="user-menu-trigger"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('auth.accountMenu')}
        data-testid="user-menu-trigger"
      >
        <span className="user-avatar" aria-hidden="true">{initials}</span>
        <span className="user-menu-name">{displayName}</span>
        <ChevronDownIcon className={`user-menu-chevron${open ? ' is-open' : ''}`} />
      </button>

      {open && (
        <div className="user-menu-panel" data-testid="user-menu-panel">
          <div className="user-menu-header">
            <span className="user-avatar user-avatar--lg" aria-hidden="true">{initials}</span>
            <div className="user-menu-identity">
              <span className="user-menu-fullname">{displayName}</span>
              {user.email && <span className="user-menu-email">{user.email}</span>}
              {user.preferredUsername && user.preferredUsername !== displayName && (
                <span className="user-menu-username">@{user.preferredUsername}</span>
              )}
              {/* Which side of the house — not a role, so it stays when the
                  role list goes: it is the one thing that explains why the
                  menu on the left looks the way it does. */}
              <span className="user-menu-chip is-space">
                {isAnepStaff ? t('auth.spaceAnep') : t('auth.spaceClient')}
              </span>
            </div>
          </div>

          <div className="user-menu-section user-menu-actions">
            <button
              type="button"
              className="user-menu-signout"
              onClick={() => {
                setOpen(false);
                logout();
              }}
              data-testid="btn-signout"
            >
              <LogOutIcon />
              <span>{t('auth.signOut')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
